<template>
  <div class="chead">
    <div class="chead__top">
      <button
        type="button"
        class="chead__back"
        :aria-label="$t('classHeader.back')"
        @click="$emit('back')"
      >
        <v-icon size="20" color="var(--dk-ink, #231A2E)">mdi-arrow-left</v-icon>
      </button>

      <div class="chead__id">
        <div class="chead__eyebrow">
          {{ $t("classHeader.eyebrow", { code: classData.cod || "—" }) }}
        </div>
        <h1 class="chead__level chead-display">{{ levelLabel }}</h1>
      </div>

      <span class="chead__status" :class="`chead__status--${statusMeta.tone}`">
        <span class="chead__status-dot"></span>{{ statusMeta.label }}
      </span>
    </div>

    <div class="chead__body">
      <div class="chead__schedule">
        <div class="chead__week" :aria-label="scheduleSummary">
          <div
            v-for="day in 7"
            :key="`d-${day}`"
            class="chead__cell"
            :class="{ 'chead__cell--on': meetsOn(day - 1) }"
          >
            {{ $t(`enums.weekLetter.${day - 1}`) }}
          </div>
        </div>
        <div class="chead__sched-text">{{ scheduleSummary }}</div>
      </div>

      <div class="chead__facts">
        <span class="chead__fact">{{ formatLabel }}</span>
        <span class="chead__dot">·</span>
        <span class="chead__fact">{{ frequencyLabel }}</span>

        <div class="chead__people">
          <div v-if="stackMembers.length" class="chead__stack">
            <span
              v-for="(member, i) in stackMembers"
              :key="member.id || i"
              class="chead__avatar"
              :title="member.name"
            >
              <img
                v-if="member.avatar"
                :src="member.avatar"
                :alt="member.name"
              />
              <span v-else>{{ memberInitials(member.name) }}</span>
            </span>
            <span
              v-if="membersCount > 4"
              class="chead__avatar chead__avatar--more"
              >+{{ membersCount - 4 }}</span
            >
          </div>
          <span class="chead__people-count">{{
            $tc("classHeader.activeStudents", membersCount, {
              count: membersCount,
            })
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  displayGroupFormat,
  displayGroupFrequency,
  displayGroupLevel,
  formatScheduleSummary,
  initialsFromName,
  normalizeClassTimes,
} from "@/shared/classPresentation";

const STATUS_META = {
  0: { label: "classHeader.forming", tone: "waiting" },
  1: { label: "classHeader.active", tone: "active" },
  2: { label: "classHeader.closed", tone: "off" },
};

export default {
  name: "ClassHeader",

  props: {
    classData: {
      type: Object,
      required: true,
    },
    theme: {
      type: Object,
      required: true,
    },
    members: {
      type: Array,
      default: () => [],
    },
    membersCount: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    levelLabel() {
      return displayGroupLevel(this.classData.level);
    },
    formatLabel() {
      return displayGroupFormat(this.classData.format);
    },
    frequencyLabel() {
      return displayGroupFrequency(this.classData.frequency);
    },
    statusMeta() {
      const meta = STATUS_META[+this.classData.status];
      if (!meta) {
        return {
          label: this.$t("enums.participantStatus.none"),
          tone: "off",
        };
      }
      return { label: this.$t(meta.label), tone: meta.tone };
    },
    scheduleSummary() {
      return formatScheduleSummary(
        this.classData.weekDays,
        this.classData.hours
      );
    },
    meetingDays() {
      return normalizeClassTimes(
        this.classData.weekDays,
        this.classData.hours
      ).map((time) => +time.weekDay);
    },
    stackMembers() {
      return (Array.isArray(this.members) ? this.members : []).slice(0, 4);
    },
  },

  methods: {
    meetsOn(weekDay) {
      return this.meetingDays.includes(weekDay);
    },
    memberInitials(name) {
      return initialsFromName(name) || "AL";
    },
  },
};
</script>

<style scoped>
.chead {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #faf7fc);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --grape: var(--dk-grape, #a826e1);
  --grape-soft: rgba(168, 38, 225, 0.1);
  --teal: var(--dk-teal, #00a379);
  --ember: var(--dk-ember, #ff5d2a);
  padding: 24px 26px;
  background: radial-gradient(
      120% 150% at 100% 0%,
      rgba(168, 38, 225, 0.09),
      transparent 55%
    ),
    var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 22px;
  box-shadow: 0 22px 48px -40px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.55);
  color: var(--ink);
}

.chead-display {
  font-family: "Poppins", sans-serif;
  letter-spacing: -0.01em;
}

.chead__top {
  display: flex;
  align-items: center;
  gap: 14px;
}
.chead__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--dk-surface, #fff);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}
.chead__back:hover {
  background: var(--paper);
}
.chead__back:focus-visible {
  outline: 2px solid var(--grape);
  outline-offset: 2px;
}
.chead__id {
  flex: 1 1 auto;
  min-width: 0;
}
.chead__eyebrow {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--grape);
}
.chead__level {
  margin: 2px 0 0;
  font-size: clamp(1.7rem, 3.6vw, 2.4rem);
  font-weight: 700;
  line-height: 1.05;
  color: var(--ink);
}
.chead__status {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.chead__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.chead__status--active {
  background: rgba(0, 163, 121, 0.12);
  color: var(--dk-teal-deep, #007f5e);
}
.chead__status--waiting {
  background: rgba(255, 93, 42, 0.12);
  color: var(--dk-ember-deep, #b5421e);
}
.chead__status--off {
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.07);
  color: var(--muted);
}

.chead__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-top: 20px;
  flex-wrap: wrap;
}
.chead__schedule {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.chead__week {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}
.chead__cell {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--muted);
  background: var(--paper);
  border: 1px solid var(--line);
}
.chead__cell--on {
  color: #fff;
  background: linear-gradient(
    160deg,
    var(--dk-grape, #b951e7),
    var(--dk-grape, #a826e1)
  );
  border-color: transparent;
  box-shadow: 0 8px 16px -8px rgba(168, 38, 225, 0.7);
}
.chead__sched-text {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

.chead__facts {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.chead__fact {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--muted);
}
.chead__dot {
  color: var(--line);
}
.chead__people {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: 6px;
  padding-left: 16px;
  border-left: 1px solid var(--line);
}
.chead__stack {
  display: flex;
}
.chead__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    160deg,
    var(--dk-grape, #c56eeb),
    var(--dk-grape, #a826e1)
  );
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  border: 2px solid var(--dk-surface, #fff);
  margin-left: -8px;
}
.chead__avatar:first-child {
  margin-left: 0;
}
.chead__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.chead__avatar--more {
  background: var(--grape-soft);
  color: var(--grape);
}
.chead__people-count {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--muted);
  white-space: nowrap;
}

@media (max-width: 600px) {
  .chead__people {
    margin-left: 0;
    padding-left: 0;
    border-left: none;
  }
}
</style>
